import { parse, type MediaType } from 'media-typer';

import {
  DISCOVERY_CONTENT_TYPE_DEFINITIONS,
  NO_MIME_TYPE,
} from './discovery-content-types';
import { isGrpcWebTextContentType } from './grpc-web-detect';

export { NO_MIME_TYPE } from './discovery-content-types';

export const RESPONSE_CONTENT_TYPES_TO_TRACK =
  DISCOVERY_CONTENT_TYPE_DEFINITIONS.filter(
    (definition) =>
      definition.defaultCrawlerEnabled &&
      definition.defaultTorchTrackingEnabled,
  ).map((definition) => definition.mimeType);

export const REQUEST_BODY_CONTENT_TYPES_TO_TRACK = [
  ...RESPONSE_CONTENT_TYPES_TO_TRACK,
  'text/plain',
  'text/javascript',
];

export const FEATURE_ANALYSIS_GET_RESPONSE_TYPES =
  RESPONSE_CONTENT_TYPES_TO_TRACK.filter((type) => type !== 'text/html');

const TEXT_MIME_TYPES = [
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-www-form-urlencoded',
  'application/graphql',
  'application/soap+xml',
] as const;

const TEXT_MIME_SUFFIXES = ['json', 'xml'] as const;

export const PROBE_HTML_CONTENT_TYPES = ['text/html'] as const;

function safeParse(contentType: string): MediaType | undefined {
  if (!contentType) {
    return undefined;
  }

  const mediaType = contentType.split(';')[0].trim();

  try {
    return parse(mediaType);
  } catch {
    return undefined;
  }
}

function hasDeclaredMimeType(
  contentType: string | undefined,
): contentType is string {
  return contentType !== undefined && contentType.trim() !== '';
}

export function isTextMimeType(
  contentType: string | string[] | undefined,
): boolean {
  if (!contentType) return true;

  const type = Array.isArray(contentType) ? contentType[0] : contentType;
  const normalized = type.split(';')[0].trim().toLowerCase();

  if (!normalized) return true;
  if (normalized.startsWith('text/')) return true;
  if (isGrpcWebTextContentType(normalized)) return true;
  if (TEXT_MIME_TYPES.some((textType) => normalized === textType)) return true;
  if (
    normalized.startsWith('application/') &&
    TEXT_MIME_SUFFIXES.some((suffix) => normalized.endsWith(`+${suffix}`))
  ) {
    return true;
  }

  return false;
}

export function matchesAnyContentTypeSemantically(
  contentType: string,
  targetTypes: readonly string[],
): boolean {
  return targetTypes.some((target) =>
    matchesContentTypeSemantically(contentType, target),
  );
}

export function shouldTrackResponseAgainstAllowlist(
  contentType: string | undefined,
  allowlist: readonly string[],
): boolean {
  const allowMissingMime = allowlist.includes(NO_MIME_TYPE);
  const mimeAllowlist = allowlist.filter((type) => type !== NO_MIME_TYPE);

  if (!hasDeclaredMimeType(contentType)) {
    return allowMissingMime;
  }

  return matchesAnyContentTypeSemantically(contentType, mimeAllowlist);
}

export function shouldTrackResponseContentType(
  contentType: string,
  extraMimeTypes?: readonly string[],
): boolean {
  if (!contentType) return false;
  const allMimeTypes = extraMimeTypes
    ? [...new Set([...RESPONSE_CONTENT_TYPES_TO_TRACK, ...extraMimeTypes])]
    : RESPONSE_CONTENT_TYPES_TO_TRACK;

  return shouldTrackResponseAgainstAllowlist(contentType, allMimeTypes);
}

export function shouldTrackRequestBodyContentType(
  contentType: string,
): boolean {
  if (!contentType) return false;

  return matchesAnyContentTypeSemantically(
    contentType,
    REQUEST_BODY_CONTENT_TYPES_TO_TRACK,
  );
}

export function shouldTrackHarEntryMimePair({
  responseMimeType,
  requestBodyMimeType,
  extraResponseMimeTypes,
}: {
  responseMimeType?: string;
  requestBodyMimeType?: string;
  extraResponseMimeTypes?: readonly string[];
}): boolean {
  if (extraResponseMimeTypes === undefined) {
    return true;
  }

  if (extraResponseMimeTypes.length === 0) {
    return false;
  }

  const trackResponse = shouldTrackResponseAgainstAllowlist(
    responseMimeType,
    extraResponseMimeTypes,
  );
  const trackBody =
    !!requestBodyMimeType &&
    shouldTrackResponseAgainstAllowlist(
      requestBodyMimeType,
      extraResponseMimeTypes,
    );

  return trackResponse || trackBody;
}

function matchesContentTypeSemantically(
  contentType: string,
  targetType: string,
): boolean {
  const parsed = safeParse(contentType);
  const parsedTarget = safeParse(targetType);

  if (!parsed || !parsedTarget) return false;

  if (
    parsed.type === parsedTarget.type &&
    parsed.subtype === parsedTarget.subtype &&
    parsed.suffix === parsedTarget.suffix
  ) {
    return true;
  }

  if (parsed.suffix && parsedTarget.suffix) {
    return parsed.suffix === parsedTarget.suffix;
  }

  if (parsed.suffix && !parsedTarget.suffix) {
    if (
      parsed.type === parsedTarget.type &&
      parsed.suffix === parsedTarget.subtype
    ) {
      return true;
    }
  }

  if (grpcWebFamilyMatches(parsed.subtype, parsedTarget.subtype)) {
    return true;
  }

  return false;
}

function grpcWebFamilyMatches(subtype: string, targetSubtype: string): boolean {
  const left = grpcWebFamily(subtype);
  const right = grpcWebFamily(targetSubtype);

  return left !== null && left === right;
}

function grpcWebFamily(
  subtype: string,
): 'grpc-web' | 'grpc-web-text' | 'grpc' | null {
  if (subtype.startsWith('grpc-web-text')) {
    return 'grpc-web-text';
  }

  if (subtype.startsWith('grpc-web')) {
    return 'grpc-web';
  }

  if (subtype === 'grpc') {
    return 'grpc';
  }

  return null;
}
